import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { ContentFilterKeyName, ContentFiltersKeys, ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { contentTypeFilterSelected } from '../../actions/spaces/ContentExplorerFilterActions';
import { mixedContentFilterApplied } from '../../actions/spaces/filters/FilterActions';
import { sortContent } from '../../actions/spaces/SortContentAction';
import { updateBreadcrumbHref } from '../../actions/UpdateBreadcrumbHref';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues } from '../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../selectors/ContentSelectors';
import { AppState } from '../../state/AppState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { getContentTypesFromQueryParam, getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';

export interface QueryParamUpdateProviderStateProps {
  readonly contentTypes: ReadonlyArray<ExplorableItemType>;
  readonly spaceInfo: SpaceInfo;
  readonly defaultContentTypeValues: ReadonlyArray<ExplorableItemType>;
}

export interface QueryParamUpdateProviderDispatchProps {
  readonly sortContent: (order: IOrderClause[]) => void;
  readonly contentTypeFilterSelected: (contentTypes: ReadonlyArray<ExplorableItemType>, clearAndSet: boolean) => void;
  readonly mixedContentFilterApplied: (filterType: ContentFilterKeyName, value: string) => void;
  readonly updateBreadcrumbHref: (url: string) => void;
}

export interface QueryParamUpdateProviderOwnProps {
  readonly updateFilters: boolean;
  readonly updateContentTypes: boolean;
  readonly updateBreadcrumbs: boolean;
  readonly updateSort: boolean;
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
}

export type QueryParamUpdateProviderProps = QueryParamUpdateProviderStateProps & QueryParamUpdateProviderDispatchProps & QueryParamUpdateProviderOwnProps;

export class QueryParamUpdateProvider extends React.PureComponent<QueryParamUpdateProviderProps> {

  public render() {
    return <>{this.props.children}</>;
  }

  public componentDidUpdate(prevProps: QueryParamUpdateProviderProps) {
    if (prevProps.routeLocationPath !== this.props.routeLocationPath) {
      // only update redux store on query-only changes.
      // There are some weird timing issue with redux and componentDidUpdate calls
      // if the route/path changes then those should be handled by Providers and children.
      // this is also why we don't update on mount.
      return;
    }

    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);
    const previousQueryValues = queryString.parse(prevProps.routeLocationSearch);

    if (this.props.updateSort && currentQueryValues[UrlParamKeys.Order] !== previousQueryValues[UrlParamKeys.Order]) {
      const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(this.props.contentTypes, this.props.spaceInfo);
      const orderFromQuery = getOrderClausesFromQueryParam(currentQueryValues[UrlParamKeys.Order], defaultOrder);
      this.props.sortContent(orderFromQuery);
    }

    if (this.props.updateFilters) {
      for (const filterType of _.keys(ContentFiltersKeys)) {
        if (currentQueryValues[filterType] !== previousQueryValues[filterType]) {
          let newValue = currentQueryValues[filterType];
          if (newValue && !_.isString(newValue)) {
            newValue = String(newValue);
          }
          this.props.mixedContentFilterApplied(filterType as ContentFilterKeyName, newValue);
        }
      }
    }

    if (this.props.updateContentTypes && currentQueryValues[ContentTypeFilterKey] !== previousQueryValues[ContentTypeFilterKey]) {
      const defaultValues = this.props.defaultContentTypeValues;
      this.props.contentTypeFilterSelected(getContentTypesFromQueryParam(currentQueryValues[ContentTypeFilterKey], defaultValues), null);
    }

    if (this.props.updateBreadcrumbs) {
      this.props.updateBreadcrumbHref(window.location.href);
    }
  }
}

const mapStateToProps: MapStateToProps<QueryParamUpdateProviderStateProps> = (state: AppState) => ({
  contentTypes: selectCurrentlyApplicableContentTypes(state),
  spaceInfo: selectSpaceInfo(state),
  defaultContentTypeValues: selectDefaultContentTypeFilterValues(state)
});

const mapDispatchToProps: MapDispatchToProps<QueryParamUpdateProviderDispatchProps> = (dispatch) => bindActionCreators({
  sortContent: sortContent,
  contentTypeFilterSelected: contentTypeFilterSelected,
  mixedContentFilterApplied: mixedContentFilterApplied,
  updateBreadcrumbHref: updateBreadcrumbHref
}, dispatch);

export const ConnectedQueryParamUpdateProvider: React.ComponentClass<QueryParamUpdateProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(QueryParamUpdateProvider);
